import { useState } from "react";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import {
  AppBar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AppLogo from "../components/AppLogo";
import abosLogoClean from "../assets/brand/abos-logo-clean.png";
import abosBrandConcept from "../assets/brand/abos-brand-concept.png";

const NAV_ITEMS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how-it-works" },
  { label: "AI", href: "#ai" },
  { label: "Plans", href: "#plans" },
];

const FEATURES = [
  {
    icon: ChatRoundedIcon,
    title: "AI customer conversations",
    body: "Let ABOS answer real customer questions using your catalog, business rules, and conversation context.",
  },
  {
    icon: MicRoundedIcon,
    title: "Voice-note understanding",
    body: "Customers can send WhatsApp voice notes. ABOS transcribes them and continues through the same conversation flow.",
  },
  {
    icon: Inventory2RoundedIcon,
    title: "Products & inventory",
    body: "Keep product information, availability, stock, and operational decisions connected to one source of truth.",
  },
  {
    icon: PointOfSaleRoundedIcon,
    title: "Sales & POS",
    body: "Run day-to-day sales operations with a workspace designed around the store, not a collection of disconnected tools.",
  },
  {
    icon: PeopleAltRoundedIcon,
    title: "Customers & orders",
    body: "Move from a customer conversation into a grounded order flow while preserving the context that got you there.",
  },
  {
    icon: AnalyticsRoundedIcon,
    title: "Reports & business visibility",
    body: "Bring operational data into one place so the store can understand what is happening without switching systems.",
  },
];

const FLOW_STEPS = [
  {
    step: "01",
    title: "Connect your business",
    body: "Bring in products, stock, store settings, and the operational data ABOS is allowed to use.",
  },
  {
    step: "02",
    title: "Connect customer channels",
    body: "Use WhatsApp as a natural customer-facing surface while your business workspace stays authoritative.",
  },
  {
    step: "03",
    title: "ABOS understands the conversation",
    body: "Memory and context help ABOS understand references, follow-ups, and what the customer is actually trying to do.",
  },
  {
    step: "04",
    title: "Business tools verify reality",
    body: "Prices, stock, products, checkout rules, and orders are verified through real backend tools before ABOS responds or acts.",
  },
];

const AI_POINTS = [
  "Arabic, Arabizi, and English conversations",
  "Conversation memory that survives natural follow-ups",
  "Grounded product, price, and availability answers",
  "Voice-note input through secure speech-to-text",
  "Product-image replies when a catalog image exists",
  "Controlled order flows backed by real business validation",
];

const planCards = [
  {
    title: "Starter",
    price: "$60/month",
    features: ["500 AI Operations", "10 Voice Minutes", "Basic Admin AI"],
  },
  {
    title: "Professional",
    price: "$80/month",
    features: ["1,500 AI Operations", "20 Voice Minutes", "Basic Admin AI", "Admin AI Agent"],
  },
  {
    title: "Business",
    price: "$100/month",
    features: ["2,500 AI Operations", "45 Voice Minutes", "Basic Admin AI", "Admin AI Agent"],
  },
];

function NavLinks({ onNavigate, mobile = false }) {
  return (
    <Stack direction={mobile ? "column" : "row"} spacing={mobile ? 0.5 : 0.75}>
      {NAV_ITEMS.map((item) => (
        <Button
          key={item.href}
          component="a"
          href={item.href}
          onClick={onNavigate}
          sx={{
            color: mobile ? "#0f172a" : "#cbd5e1",
            justifyContent: mobile ? "flex-start" : "center",
            px: 1.5,
            "&:hover": {
              color: mobile ? "#2563eb" : "white",
              bgcolor: mobile ? "#eff6ff" : "rgba(255,255,255,0.06)",
            },
          }}
        >
          {item.label}
        </Button>
      ))}
    </Stack>
  );
}

function FeatureCard({ feature }) {
  const Icon = feature.icon;
  return (
    <Card
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 4,
        borderColor: "#e7edf5",
        boxShadow: "0 18px 55px rgba(15, 23, 42, 0.06)",
        transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: "#bfdbfe",
          boxShadow: "0 22px 65px rgba(37, 99, 235, 0.11)",
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 3,
          display: "grid",
          placeItems: "center",
          color: "#2563eb",
          bgcolor: "#eff6ff",
          mb: 2.25,
        }}
      >
        <Icon />
      </Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {feature.title}
      </Typography>
      <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
        {feature.body}
      </Typography>
    </Card>
  );
}

function HeroVisual() {
  const orbitItems = [
    { label: "WhatsApp", sx: { top: { xs: "7%", sm: "8%" }, left: { xs: "4%", sm: "5%" } } },
    { label: "Voice", sx: { top: { xs: "9%", sm: "10%" }, right: { xs: "4%", sm: "5%" } } },
    { label: "Inventory", sx: { top: { xs: "65%", sm: "69%" }, left: { xs: "3%", sm: "4%" } } },
    { label: "Orders", sx: { top: { xs: "65%", sm: "69%" }, right: { xs: "3%", sm: "4%" } } },
  ];

  return (
    <Box
      aria-label="ABOS connected business system"
      sx={{
        minHeight: { xs: 390, md: 500 },
        position: "relative",
        borderRadius: { xs: 5, md: 7 },
        overflow: "hidden",
        border: "1px solid rgba(125, 211, 252, 0.16)",
        background:
          "radial-gradient(circle at 50% 44%, rgba(20,184,166,.22), transparent 27%), radial-gradient(circle at 24% 25%, rgba(37,99,235,.26), transparent 34%), linear-gradient(145deg, #07111f 0%, #071827 58%, #06131b 100%)",
        boxShadow: "0 42px 110px rgba(0,0,0,.38)",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: { xs: "11% 8%", sm: "11%" },
          borderRadius: "50%",
          border: "1px solid rgba(45,212,191,.15)",
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: { xs: "24% 19%", sm: "24%" },
          borderRadius: "50%",
          border: "1px dashed rgba(96,165,250,.2)",
        }}
      />

      {orbitItems.map((item) => (
        <Chip
          key={item.label}
          label={item.label}
          sx={{
            position: "absolute",
            zIndex: 2,
            ...item.sx,
            color: "#e2e8f0",
            bgcolor: "rgba(7, 24, 39, 0.86)",
            border: "1px solid rgba(148,163,184,.22)",
            backdropFilter: "blur(12px)",
            fontWeight: 800,
          }}
        />
      ))}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          pb: { xs: 3, sm: 2 },
        }}
      >
        <Box
          sx={{
            width: { xs: 196, sm: 258 },
            height: { xs: 196, sm: 258 },
            borderRadius: "50%",
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(0,0,0,.70)",
            border: "1px solid rgba(96,165,250,.15)",
            boxShadow: "0 34px 84px rgba(0,0,0,.42), 0 0 0 10px rgba(2,6,23,.20)",
          }}
        >
          <Box
            component="img"
            src={abosLogoClean}
            alt="ABOS logo"
            sx={{
              display: "block",
              width: "98%",
              height: "98%",
              objectFit: "contain",
              filter: "drop-shadow(0 18px 30px rgba(0,0,0,.28))",
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: "absolute",
          zIndex: 3,
          left: { xs: 16, sm: 30 },
          right: { xs: 16, sm: 30 },
          bottom: { xs: 14, sm: 20 },
          px: { xs: 1.8, sm: 2.2 },
          py: { xs: 1.35, sm: 1.5 },
          borderRadius: 3.5,
          bgcolor: "rgba(2, 6, 23, 0.82)",
          border: "1px solid rgba(255,255,255,.08)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#14b8a6", boxShadow: "0 0 16px #14b8a6", flexShrink: 0 }} />
          <Typography sx={{ color: "#dbeafe", fontWeight: 800, fontSize: { xs: 12.5, sm: 14 }, lineHeight: 1.35 }}>
            Conversation context + real business tools
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default function LandingPage() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box sx={{ bgcolor: "#fff", color: "#0f172a", minHeight: "100vh" }}>
      <Box
        component="header"
        sx={{
          position: "relative",
          overflow: "hidden",
          color: "white",
          background:
            "radial-gradient(circle at 15% 15%, rgba(37,99,235,.30), transparent 27%), radial-gradient(circle at 84% 9%, rgba(20,184,166,.22), transparent 28%), linear-gradient(145deg, #030b16 0%, #071523 52%, #071a21 100%)",
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "rgba(3, 11, 22, .78)",
            borderBottom: "1px solid rgba(148,163,184,.12)",
            backdropFilter: "blur(18px)",
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ minHeight: { xs: 72, md: 78 }, gap: 2 }}>
              <Box component={RouterLink} to="/" sx={{ textDecoration: "none", flexShrink: 0 }}>
                <AppLogo light />
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" }, ml: "auto" }}>
                <NavLinks />
              </Box>

              <Stack direction="row" spacing={1} sx={{ ml: { xs: "auto", md: 2 }, display: { xs: "none", sm: "flex" } }}>
                <Button component={RouterLink} to="/login" sx={{ color: "#e2e8f0" }}>
                  Sign in
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    bgcolor: "#fff",
                    color: "#071827",
                    backgroundImage: "none",
                    "&:hover": { bgcolor: "#eaf6ff", backgroundImage: "none" },
                  }}
                >
                  Get started
                </Button>
              </Stack>

              <IconButton
                aria-label="Open navigation"
                onClick={() => setMobileOpen(true)}
                sx={{ color: "white", ml: "auto", display: { xs: "inline-flex", md: "none" } }}
              >
                <MenuRoundedIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              py: { xs: 8, sm: 10, md: 13 },
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.02fr) minmax(420px, .98fr)" },
              gap: { xs: 7, lg: 8 },
              alignItems: "center",
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Chip
                icon={<AutoAwesomeRoundedIcon sx={{ color: "#67e8f9 !important" }} />}
                label="AI BUSINESS OPERATING SYSTEM"
                sx={{
                  color: "#cffafe",
                  bgcolor: "rgba(8,145,178,.12)",
                  border: "1px solid rgba(103,232,249,.2)",
                  mb: 3,
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "3.25rem", sm: "4.3rem", lg: "5.35rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.055em",
                  lineHeight: 0.98,
                  maxWidth: 820,
                }}
              >
                One system.
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    background: "linear-gradient(90deg, #60a5fa 0%, #22d3ee 45%, #2dd4bf 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  All your business.
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: "#b7c7d8",
                  fontSize: { xs: "1.05rem", sm: "1.22rem" },
                  lineHeight: 1.8,
                  maxWidth: 690,
                  mt: 3,
                }}
              >
                ABOS connects your store operations with an AI employee that can understand customer conversations, work with your real business data, and keep the flow moving from question to action.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    minHeight: 54,
                    px: 3,
                    background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 52%, #14b8a6 100%)",
                    boxShadow: "0 18px 44px rgba(37,99,235,.32)",
                  }}
                >
                  Create your workspace
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  size="large"
                  sx={{ minHeight: 54, px: 3, color: "white", borderColor: "rgba(255,255,255,.22)", "&:hover": { borderColor: "rgba(255,255,255,.42)", bgcolor: "rgba(255,255,255,.04)" } }}
                >
                  Sign in
                </Button>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 3 }} sx={{ mt: 4 }}>
                {["Grounded in real store data", "Built for WhatsApp conversations", "Secure multi-tenant workspace"].map((item) => (
                  <Stack direction="row" spacing={0.9} alignItems="center" key={item}>
                    <CheckCircleRoundedIcon sx={{ color: "#2dd4bf", fontSize: 19 }} />
                    <Typography sx={{ color: "#d8e4ef", fontSize: 14, fontWeight: 700 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <HeroVisual />
          </Box>
        </Container>
      </Box>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 310, p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <AppLogo />
            <IconButton aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <NavLinks mobile onNavigate={() => setMobileOpen(false)} />
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Button component={RouterLink} to="/login" variant="outlined" onClick={() => setMobileOpen(false)}>
              Sign in
            </Button>
            <Button component={RouterLink} to="/register" variant="contained" onClick={() => setMobileOpen(false)}>
              Get started
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <Box component="main">
        <Box id="features" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
          <Container maxWidth="xl">
            <Box sx={{ maxWidth: 760, mb: 5 }}>
              <Typography variant="overline" sx={{ color: "#0f766e", fontWeight: 950, letterSpacing: ".12em" }}>
                ONE OPERATING WORKSPACE
              </Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: "2.3rem", md: "3.4rem" }, mt: 1 }}>
                Everything your store needs to stay connected.
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: "1.08rem", lineHeight: 1.8, mt: 1.8 }}>
                ABOS brings customer conversations and day-to-day operations together so the AI can understand the request while your backend remains the source of business truth.
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: 2.25 }}>
              {FEATURES.map((feature) => <FeatureCard key={feature.title} feature={feature} />)}
            </Box>
          </Container>
        </Box>

        <Box id="how-it-works" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "#fff" }}>
          <Container maxWidth="xl">
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: ".88fr 1.12fr" }, gap: { xs: 6, lg: 9 }, alignItems: "center" }}>
              <Box>
                <Typography variant="overline" sx={{ color: "#2563eb", fontWeight: 950, letterSpacing: ".12em" }}>
                  HOW ABOS WORKS
                </Typography>
                <Typography variant="h2" sx={{ fontSize: { xs: "2.3rem", md: "3.4rem" }, mt: 1 }}>
                  AI understanding on top. Business truth underneath.
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: "1.08rem", lineHeight: 1.85, mt: 2 }}>
                  ABOS is designed so the AI can understand natural conversation and memory while products, inventory, pricing, checkout rules, and orders stay grounded in your real system.
                </Typography>

                <Box sx={{ mt: 4 }}>
                  {FLOW_STEPS.map((item, index) => (
                    <Box key={item.step}>
                      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 2.1 }}>
                        <Box sx={{ minWidth: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: index === 2 ? "#ecfeff" : "#eff6ff", color: index === 2 ? "#0f766e" : "#2563eb", fontWeight: 950 }}>
                          {item.step}
                        </Box>
                        <Box>
                          <Typography variant="h6">{item.title}</Typography>
                          <Typography color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.7 }}>{item.body}</Typography>
                        </Box>
                      </Stack>
                      {index < FLOW_STEPS.length - 1 && <Divider />}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  p: { xs: 1.3, sm: 2 },
                  borderRadius: 6,
                  bgcolor: "#061420",
                  border: "1px solid #dbeafe",
                  boxShadow: "0 35px 90px rgba(15,23,42,.16)",
                }}
              >
                <Box component="img" src={abosBrandConcept} alt="ABOS business system concept" loading="lazy" sx={{ display: "block", width: "100%", borderRadius: 4.5 }} />
              </Box>
            </Box>
          </Container>
        </Box>

        <Box id="ai" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "#06111d", color: "white", position: "relative", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", bgcolor: "rgba(37,99,235,.13)", filter: "blur(10px)", right: -170, top: -240 }} />
          <Container maxWidth="xl" sx={{ position: "relative" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.02fr .98fr" }, gap: { xs: 6, lg: 10 }, alignItems: "center" }}>
              <Box>
                <Typography variant="overline" sx={{ color: "#5eead4", fontWeight: 950, letterSpacing: ".12em" }}>
                  THE ABOS AI
                </Typography>
                <Typography variant="h2" sx={{ fontSize: { xs: "2.35rem", md: "3.55rem" }, mt: 1 }}>
                  A conversation that stays connected to the business.
                </Typography>
                <Typography sx={{ color: "#aebfd0", fontSize: "1.08rem", lineHeight: 1.85, mt: 2, maxWidth: 720 }}>
                  Customers can ask naturally, switch between text and voice, reference products from earlier turns, request images, and continue into an order. ABOS keeps the conversation context while the backend verifies every business-critical fact.
                </Typography>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.4, mt: 4 }}>
                  {AI_POINTS.map((point) => (
                    <Stack key={point} direction="row" spacing={1.2} alignItems="flex-start" sx={{ p: 1.6, borderRadius: 3, bgcolor: "rgba(255,255,255,.035)", border: "1px solid rgba(255,255,255,.07)" }}>
                      <CheckCircleRoundedIcon sx={{ color: "#2dd4bf", fontSize: 20, mt: 0.15 }} />
                      <Typography sx={{ color: "#e2e8f0", lineHeight: 1.6, fontWeight: 650 }}>{point}</Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>

              <Box sx={{ position: "relative", minHeight: { xs: 430, sm: 500 } }}>
                <Card sx={{ position: "absolute", inset: { xs: "0 0 88px 0", sm: "0 8% 72px 0" }, bgcolor: "#0b1a29", color: "white", borderColor: "rgba(148,163,184,.16)", borderRadius: 5, p: { xs: 2, sm: 3 }, boxShadow: "0 34px 90px rgba(0,0,0,.32)" }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box component="img" src={abosLogoClean} alt="" aria-hidden="true" sx={{ width: 42, height: 42, objectFit: "contain", bgcolor: "#000", borderRadius: 2.2 }} />
                    <Box>
                      <Typography fontWeight={900}>ABOS customer AI</Typography>
                      <Typography variant="caption" sx={{ color: "#7dd3fc" }}>Connected to your store data</Typography>
                    </Box>
                  </Stack>
                  <Stack spacing={1.5} sx={{ mt: 3 }}>
                    <Box sx={{ alignSelf: "flex-start", maxWidth: "82%", bgcolor: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.06)", p: 1.7, borderRadius: "16px 16px 16px 4px" }}>
                      <Typography sx={{ color: "#e2e8f0" }}>Do you have the iPhone 16 Pro, and can I see its price and picture?</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-end", maxWidth: "88%", bgcolor: "rgba(37,99,235,.19)", border: "1px solid rgba(96,165,250,.18)", p: 1.7, borderRadius: "16px 16px 4px 16px" }}>
                      <Typography sx={{ color: "#dbeafe" }}>ABOS checks the real catalog, answers with grounded product information, and can attach the stored product image.</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-start", maxWidth: "68%", bgcolor: "rgba(255,255,255,.07)", p: 1.7, borderRadius: "16px 16px 16px 4px" }}>
                      <Typography sx={{ color: "#e2e8f0" }}>Okay, I want one.</Typography>
                    </Box>
                    <Box sx={{ alignSelf: "flex-end", maxWidth: "86%", bgcolor: "rgba(20,184,166,.14)", border: "1px solid rgba(45,212,191,.16)", p: 1.7, borderRadius: "16px 16px 4px 16px" }}>
                      <Typography sx={{ color: "#ccfbf1" }}>The same conversation can continue into the store's checkout requirements without losing product context.</Typography>
                    </Box>
                  </Stack>
                </Card>

                <Card sx={{ position: "absolute", width: { xs: "70%", sm: "58%" }, right: 0, bottom: 0, bgcolor: "#fff", borderRadius: 4, p: 2.2, boxShadow: "0 28px 70px rgba(0,0,0,.32)" }}>
                  <Stack direction="row" spacing={1.1} alignItems="center">
                    <MicRoundedIcon sx={{ color: "#0f766e" }} />
                    <Box>
                      <Typography color="#0f172a" fontWeight={900}>Voice notes work too</Typography>
                      <Typography variant="caption" color="text.secondary">Speech → text → same ABOS logic</Typography>
                    </Box>
                  </Stack>
                </Card>
              </Box>
            </Box>
          </Container>
        </Box>

        <Box id="plans" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
          <Container maxWidth="xl">
            <Box sx={{ maxWidth: 760, textAlign: { xs: "left", md: "center" }, mx: { md: "auto" } }}>
              <Typography variant="overline" sx={{ color: "#2563eb", fontWeight: 950, letterSpacing: ".12em" }}>
                PLANS & CONTROL
              </Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: "2.3rem", md: "3.35rem" }, mt: 1 }}>
                AI usage that stays visible and controllable.
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: "1.08rem", lineHeight: 1.8, mt: 1.8 }}>
                ABOS plans can define independent allowances for AI operations and voice usage, with clear tracking for the store and explicit controls for platform administration.
              </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2.25, mt: 5 }}>
              {planCards.map((item, index) => (
                <Card key={item.title} sx={{ p: 3.2, borderRadius: 4, height: "100%", borderColor: index === 1 ? "#99f6e4" : "#dbe4ef", bgcolor: index === 1 ? "#f0fdfa" : "white" }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: index === 1 ? "#ccfbf1" : "#eff6ff", color: index === 1 ? "#0f766e" : "#2563eb", mb: 2 }}>
                    {index === 0 ? <AutoAwesomeRoundedIcon /> : index === 1 ? <MicRoundedIcon /> : <SecurityRoundedIcon />}
                  </Box>
                  <Typography variant="h6">{item.title}</Typography>
                  <Typography variant="h4" sx={{ mt: 1, fontWeight: 950 }}>{item.price}</Typography>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {item.features.map((feature) => (
                      <Stack key={feature} direction="row" spacing={1} alignItems="center">
                        <CheckCircleRoundedIcon color="success" fontSize="small" />
                        <Typography color="text.secondary">{feature}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Card>
              ))}
            </Box>

            <Card
              sx={{
                mt: 4,
                p: { xs: 3, md: 4.5 },
                borderRadius: 5,
                color: "white",
                bgcolor: "#071523",
                backgroundImage: "radial-gradient(circle at 85% 20%, rgba(20,184,166,.20), transparent 30%), radial-gradient(circle at 10% 80%, rgba(37,99,235,.22), transparent 34%)",
                borderColor: "rgba(15,118,110,.2)",
              }}
            >
              <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ md: "center" }} justifyContent="space-between">
                <Box sx={{ maxWidth: 760 }}>
                  <Typography variant="h4" sx={{ color: "white" }}>Ready to put ABOS around your store?</Typography>
                  <Typography sx={{ color: "#b9c8d7", lineHeight: 1.75, mt: 1.2 }}>
                    Create a workspace, connect your operational data, and bring customer conversations into one controlled business system.
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ flexShrink: 0 }}>
                  <Button component={RouterLink} to="/login" variant="outlined" sx={{ color: "white", borderColor: "rgba(255,255,255,.24)", "&:hover": { borderColor: "rgba(255,255,255,.45)" } }}>
                    Sign in
                  </Button>
                  <Button component={RouterLink} to="/register" variant="contained" endIcon={<ArrowForwardRoundedIcon />}>
                    Get started
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </Container>
        </Box>
      </Box>

      <Box component="footer" sx={{ bgcolor: "#030b16", color: "white", py: { xs: 5, md: 6 } }}>
        <Container maxWidth="xl">
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ md: "center" }} justifyContent="space-between">
            <Box>
              <AppLogo light />
              <Typography sx={{ color: "#94a3b8", mt: 1.5, maxWidth: 480 }}>
                One System. All Your Business.
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }}>
              {NAV_ITEMS.map((item) => (
                <Button key={item.href} component="a" href={item.href} sx={{ color: "#cbd5e1", justifyContent: { xs: "flex-start", sm: "center" } }}>
                  {item.label}
                </Button>
              ))}
              <Button component={RouterLink} to="/terms" sx={{ color: "#cbd5e1" }}>Terms</Button>
              <Button component={RouterLink} to="/privacy" sx={{ color: "#cbd5e1" }}>Privacy</Button>
              <Button component={RouterLink} to="/login" sx={{ color: "#cbd5e1" }}>Sign in</Button>
            </Stack>
          </Stack>
          <Divider sx={{ borderColor: "rgba(148,163,184,.14)", my: 3.5 }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
            <Typography variant="caption" sx={{ color: "#64748b" }}>ABOS — AI Business Operating System</Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>Built for connected store operations.</Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
